import { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader } from "../components/PageHeader";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import { toast } from "sonner";
import { Plus, Trash2, Users, Settings } from "lucide-react";

const empty = { customer_name: "", phone: "", guests: 2, date: "", time: "20:00", table: "", notes: "", status: "confermata" };
const statusColor = { confermata: "bg-[hsl(var(--success))]/15 text-[hsl(var(--success))]", "in attesa": "bg-[hsl(var(--warning))]/15 text-[hsl(var(--warning))]", annullata: "bg-destructive/15 text-destructive" };

export default function Reservations() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(empty);
  const [open, setOpen] = useState(false);
  const [setOpen2, setSetOpen2] = useState(false);
  const [capacity, setCapacity] = useState(40);
  const [slotsText, setSlotsText] = useState("");
  const canManage = user?.role === "admin" || user?.role === "manager";

  const load = async () => setItems((await api.get("/reservations")).data);
  useEffect(() => { load(); }, []);

  const openSettings = async () => {
    try {
      const { data } = await api.get("/settings");
      setCapacity(data.capacity);
      setSlotsText((data.slots || []).join(", "));
      setSetOpen2(true);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const saveSettings = async () => {
    const slots = slotsText.split(",").map((s) => s.trim()).filter(Boolean);
    try {
      await api.put("/settings", { capacity: parseInt(capacity), slots });
      toast.success("Impostazioni sala salvate"); setSetOpen2(false);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const save = async () => {
    if (!form.customer_name || !form.date) return toast.error("Nome e data obbligatori");
    try {
      await api.post("/reservations", { ...form, guests: parseInt(form.guests) });
      toast.success("Prenotazione salvata"); setOpen(false); setForm(empty); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const setStatus = async (r, status) => {
    await api.put(`/reservations/${r.id}`, { ...r, status });
    load();
  };
  const remove = async (id) => { await api.delete(`/reservations/${id}`); toast.success("Eliminata"); load(); };

  return (
    <div>
      <PageHeader title="Prenotazioni" subtitle="Gestisci i tavoli e gli ospiti" testid="reservations-header">
        {canManage && (
          <Button variant="outline" onClick={openSettings} className="rounded-full" data-testid="room-settings-button">
            <Settings className="w-4 h-4 mr-1" /> Impostazioni sala
          </Button>
        )}
        <Button onClick={() => { setForm(empty); setOpen(true); }} className="rounded-full" data-testid="add-reservation-button">
          <Plus className="w-4 h-4 mr-1" /> Nuova prenotazione
        </Button>
      </PageHeader>

      <Card className="rounded-xl overflow-hidden">
        <Table data-testid="reservations-table">
          <TableHeader>
            <TableRow>
              <TableHead>Cliente</TableHead><TableHead>Data</TableHead><TableHead>Ora</TableHead>
              <TableHead className="text-center">Ospiti</TableHead><TableHead>Tavolo</TableHead>
              <TableHead>Stato</TableHead><TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-8">Nessuna prenotazione</TableCell></TableRow>}
            {items.map((r) => (
              <TableRow key={r.id} data-testid={`reservation-row-${r.id}`}>
                <TableCell className="font-medium">{r.customer_name}<div className="text-xs text-muted-foreground">{r.phone}</div></TableCell>
                <TableCell>{r.date}</TableCell>
                <TableCell>{r.time}</TableCell>
                <TableCell className="text-center"><span className="inline-flex items-center gap-1"><Users className="w-3.5 h-3.5" />{r.guests}</span></TableCell>
                <TableCell>{r.table || "—"}</TableCell>
                <TableCell>
                  <Select value={r.status} onValueChange={(v) => setStatus(r, v)}>
                    <SelectTrigger className={`h-7 w-32 border-0 text-xs rounded-full ${statusColor[r.status] || ""}`} data-testid={`reservation-status-${r.id}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="confermata">Confermata</SelectItem>
                      <SelectItem value="in attesa">In attesa</SelectItem>
                      <SelectItem value="annullata">Annullata</SelectItem>
                    </SelectContent>
                  </Select>
                </TableCell>
                <TableCell><Button size="icon" variant="ghost" onClick={() => remove(r.id)} data-testid={`delete-reservation-${r.id}`}><Trash2 className="w-4 h-4 text-destructive" /></Button></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="reservation-dialog">
          <DialogHeader><DialogTitle>Nuova prenotazione</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Cliente</Label><Input value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} data-testid="res-name-input" /></div>
              <div className="space-y-2"><Label>Telefono</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="res-phone-input" /></div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2"><Label>Data</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="res-date-input" /></div>
              <div className="space-y-2"><Label>Ora</Label><Input type="time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} data-testid="res-time-input" /></div>
              <div className="space-y-2"><Label>Ospiti</Label><Input type="number" value={form.guests} onChange={(e) => setForm({ ...form, guests: e.target.value })} data-testid="res-guests-input" /></div>
            </div>
            <div className="space-y-2"><Label>Tavolo</Label><Input value={form.table} onChange={(e) => setForm({ ...form, table: e.target.value })} data-testid="res-table-input" /></div>
            <div className="space-y-2"><Label>Note</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="res-notes-input" /></div>
          </div>
          <DialogFooter><Button onClick={save} className="rounded-full" data-testid="save-reservation-button">Salva</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={setOpen2} onOpenChange={setSetOpen2}>
        <DialogContent data-testid="room-settings-dialog">
          <DialogHeader><DialogTitle>Impostazioni sala</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Coperti totali disponibili</Label>
              <Input type="number" min="1" value={capacity} onChange={(e) => setCapacity(e.target.value)} data-testid="settings-capacity-input" />
              <p className="text-xs text-muted-foreground">Numero massimo di ospiti che il locale può accogliere per fascia oraria.</p>
            </div>
            <div className="space-y-2">
              <Label>Orari prenotabili</Label>
              <Textarea value={slotsText} onChange={(e) => setSlotsText(e.target.value)} rows={3} placeholder="12:00, 12:30, 20:00, 20:30" data-testid="settings-slots-input" />
              <p className="text-xs text-muted-foreground">Separa gli orari con una virgola. I clienti vedranno solo questi orari, con i posti ancora liberi.</p>
            </div>
          </div>
          <DialogFooter><Button onClick={saveSettings} className="rounded-full" data-testid="save-settings-button">Salva</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
